import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Account } from '../../../../../../../../shared/models';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../state/app.state';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { ProjectsService } from '../../../../../../core/projects.service';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { AppService, AuthService } from '../../../../../../../../shared/services';
import { takeUntil } from 'rxjs/operators';
import { FetchLineageRequest, FetchUsersListRequest } from '../../../../../../core/projects.actions';
import { lineageList, projectDetails, usersListInfo } from '../../../../../../core/projects.selectors';
import * as moment from 'moment-timezone';

@Component({
    selector: 'app-submittals-workflow',
    templateUrl: './submittals-workflow.component.html',
    styleUrls: ['./submittals-workflow.component.scss'],
    standalone: false
})
export class SubmittalsWorkflowComponent implements OnInit, OnDestroy {
  @Input() data;
  projectId: any;
  reportTitle: any;
  timelineSteps: any[] = [];
  createdBy: void;
  account: Account;
  name: string;
  userOptions = [];
  expandedComments: Set<string> = new Set();
  projectTimezone: string;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    public route: ActivatedRoute,
    public router: Router,
    public commonService: CommonService,
    public projectsService: ProjectsService,
    public daily: CommonService,
    public dialog: MatDialog,
    public authService: AuthService,
    public appService: AppService,
  ) {
  }

  ngOnInit() {
    const url = this.router.url;
    const path = url.split('?')[0];
    const segments = path.split('/');
    this.projectId = segments[segments.length - 1];

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp && qp['snSubTab'] === 'discussion') {
          this.timelineSteps = [];
          this.store.dispatch(FetchLineageRequest({
            payload: {
              qp: {
                start: 0,
                total: 1000,
                ex: `submittal-approval/${this.data?.id}`,
                include: ['actor', 'state', 'attachments'],
              },
            },
          }));

          this.fetchUsersList();
        }
      });

    this.account = this.authService.getCurrentUser();

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.timezone) {
          this.projectTimezone = data.timezone;
        }
      });

    this.store.select(usersListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data && data.list?.length > 0) {
          this.userOptions = [...data.list];
        }
      });

    this.store.select(lineageList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data.length) {
          this.timelineSteps = data?.map(step => ({
            ...step,
            actorName: this.getName(step),
            icon: this.getIcon(step),
            approved: step?.state?.name === 'change-order-request-approve',
            submitForApproval: step?.state?.name === 'change-order-request-submit',
          }));
          this.timelineSteps.forEach(tls => {
            if (tls?.input?.formValues?._fyi_users && tls?.input?.formValues?._fyi_users?.length) {
              const fyiUsers = [];
              tls?.input?.formValues?._fyi_users.forEach(user => {
                const matchedUser = this.userOptions.find(u => u.id === user);
                if (matchedUser) {
                  fyiUsers.push(matchedUser);
                }
              });
              tls['fyi_users'] = fyiUsers || [];
            }
            if (tls?.input?.formValues?._reviewers && tls?.input?.formValues?._reviewers?.length) {
              const reviewers = [];
              tls?.input?.formValues?._reviewers.forEach(user => {
                const matchedUser = this.userOptions.find(u => u.id === user);
                if (matchedUser) {
                  reviewers.push(matchedUser);
                }
              });
              tls['reviewers'] = reviewers || [];
            }
          });
        } else {
          this.timelineSteps = [];
        }
      });

  }

  getIcon(data: any) {
    const comment = data?.state?.comment;
    if (typeof comment === 'string' && comment.toLowerCase().includes('reject')) {
      return 'reject';
    } else if (typeof comment === 'string' && comment.toLowerCase().includes('approve')) {
      return 'correct';
    } else {
      return null;
    }
  }

  getName(data) {
    const name = `${data?.actor?.first_name} ${data?.actor?.last_name}`;

    if (name === this.account.name) {
      return 'You';
    } else {
      return name;
    }
  }

  fetchUsersList(): void {
    const payload = {
      start: 0,
      total: 1000,
      include: ['roles'],
      projectId: this.projectId,
    };

    if (this.projectId) {
      this.store.dispatch(FetchUsersListRequest({ payload }));
    }
  }

  toggleComment(stepId: string): void {
    if (this.expandedComments.has(stepId)) {
      this.expandedComments.delete(stepId);
    } else {
      this.expandedComments.add(stepId);
    }
  }

  isCommentExpanded(stepId: string): boolean {
    return this.expandedComments.has(stepId);
  }

  shouldTruncateComment(comment: string, maxLength: number = 150): boolean {
    return comment && comment.length > maxLength;
  }

  getTruncatedComment(comment: string, maxLength: number = 150): string {
    if (!comment || comment.length <= maxLength) {
      return comment;
    }
    return comment.substring(0, maxLength) + '...';
  }

  formatInProjectTimezone(timestamp: string, format: string): string {
    if (!timestamp) return '';
    const tz = this.projectTimezone || 'UTC';
    return moment.utc(timestamp).tz(tz).format(format);
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
